import React from 'react';
import { FaStore, FaHistory, FaMoneyBill, FaTag, FaEdit, FaTrash } from 'react-icons/fa';
import CenowkaInlineEditor from './CenowkaInlineEditor';
import {
  simplifyProductName,
  extractPackageQuantity,
  extractWeight,
  extractManufacturer,
} from './pricingUtils';

const SortIcon = ({ sortColumn, sortDirection, column }) => {
  if (sortColumn !== column) {
    return <span style={{ opacity: 0.3, marginLeft: '2px' }}>{'↕'}</span>;
  }
  return <span style={{ marginLeft: '2px', color: 'var(--info)' }}>
    {sortDirection === 'asc' ? '↑' : '↓'}
  </span>;
};

const PricingTable = ({
  filteredProducts,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  sortColumn,
  sortDirection,
  handleSort,
  selectedProducts,
  handleSelectProduct,
  handleSelectAll,
  manufacturers,
  measureUnits,
  // Editing
  editingProductManufacturer,
  setEditingProductManufacturer,
  handleSingleManufacturerChange,
  editingCenowkaProductId,
  // Action menu
  openActionMenu,
  setOpenActionMenu,
  // Permissions
  canEditPrices,
  canPrintLabels,
  canViewHistory,
  canDeleteSpecialPrice,
  // Callbacks
  handleShowHistory,
  handleDirectPriceEdit,
  handleInlineSpecialPriceSave,
  handleEditCenowka,
  handleBulkPriceChange,
  setSelectedProducts,
  handleRemoveSpecialPrices,
  addToBuffer,
  selectedInvoiceId,
  filterTerm,
  // Cenowka inline editor props
  cenowkaEditorProps,
}) => {
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage) || 1;
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Inline edycja ceny specjalnej (dwuklik na komorce): edytujemy BRUTTO.
  // Enter/blur = zapis, Escape = anuluj. Start od ceny spec. (lub domyslnej gdy brak).
  const [editingPriceProductId, setEditingPriceProductId] = React.useState(null);
  const [editingPriceValue, setEditingPriceValue] = React.useState('');
  const [savingPrice, setSavingPrice] = React.useState(false);

  const startInlinePriceEdit = (product) => {
    if (!canEditPrices || !handleInlineSpecialPriceSave) return;
    setEditingPriceProductId(product.id);
    const base = product.hasSpecialPrice ? product.specialPriceBrutto : product.cena_sprzedazy_brutto;
    setEditingPriceValue(base != null ? parseFloat(base).toFixed(2) : '');
  };

  const commitInlinePriceEdit = async (product) => {
    if (savingPrice) return;
    const raw = String(editingPriceValue).trim();
    // Bez zmian lub pusto -> po prostu zamknij edycje
    const current = product.hasSpecialPrice ? product.specialPriceBrutto : product.cena_sprzedazy_brutto;
    if (!raw || parseFloat(raw.replace(',', '.')) === parseFloat(current ?? NaN)) {
      setEditingPriceProductId(null); return;
    }
    setSavingPrice(true);
    const ok = await handleInlineSpecialPriceSave(product, raw);
    setSavingPrice(false);
    if (ok) setEditingPriceProductId(null);
  };

  if (filteredProducts.length === 0) {
    return (
      <div style={{ padding: "0.5rem" }}>
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          <FaStore style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
          <div style={{ fontSize: '13px', fontWeight: '500' }}>Brak produktów</div>
          <div style={{ fontSize: '11px' }}>{selectedInvoiceId ? 'Brak produktów pasujących do faktury' : (filterTerm ? 'Brak produktów spełniających kryteria' : 'Magazyn jest pusty')}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "0.5rem" }}>
      {/* Paginacja górna */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pokaż:</span>
          <select
            style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>produktów</span>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          Wyświetlono {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredProducts.length)} z {filteredProducts.length} produktów
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)', opacity: currentPage === 1 ? 0.5 : 1 }}
            disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>{'«'}</button>
          <button style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)', opacity: currentPage === 1 ? 0.5 : 1 }}
            disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>{'‹'}</button>
          <span style={{ padding: '0.25rem 0.5rem', fontSize: '11px', backgroundColor: 'var(--border)', borderRadius: '4px' }}>
            {currentPage} / {totalPages}
          </span>
          <button style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)', opacity: currentPage >= totalPages ? 0.5 : 1 }}
            disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>{'›'}</button>
          <button style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'var(--card-bg)', opacity: currentPage >= totalPages ? 0.5 : 1 }}
            disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>{'»'}</button>
        </div>
      </div>

      {/* Info o sortowaniu */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.25rem 0.5rem',
        backgroundColor: 'var(--border)',
        borderRadius: '4px',
        marginBottom: '0.5rem',
        fontSize: '11px'
      }}>
        <span style={{ color: 'var(--text-muted)' }}>
          Sortowanie: <strong style={{ color: 'var(--text-secondary)' }}>
            {sortColumn === 'nazwa' ? 'Produkt' :
             sortColumn === 'producent' ? 'Producent' :
             sortColumn === 'kod' ? 'Kod/EAN' :
             sortColumn === 'opakowanie' ? 'Opakowanie' :
             sortColumn === 'cena_zakupu' ? 'Cena zakupu' :
             sortColumn === 'cena_sprzedazy' ? 'Cena sprzedaży' :
             sortColumn === 'marza' ? 'Marża' : sortColumn}
          </strong> ({sortDirection === 'asc' ? 'A→Z / rosnąco' : 'Z→A / malejąco'})
        </span>
        {sortColumn !== 'nazwa' && (
          <button
            onClick={() => handleSort('nazwa')}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              border: '1px solid var(--text-muted)',
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-muted)'
            }}
          >
            {'✕'} Resetuj sortowanie
          </button>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '850px', fontSize: '10px', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: 'var(--bg)' }}>
            <tr>
              <th style={{ width: '30px', padding: '0.3rem 0.2rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>
                <input type="checkbox" checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0} onChange={handleSelectAll} style={{ width: '14px', height: '14px' }} />
              </th>
              <th style={{ width: '35px', padding: '0.3rem 0.15rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '9px' }}>Lp</th>
              <th onClick={() => handleSort('producent')} style={{ width: '90px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Producent <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="producent" />
              </th>
              <th onClick={() => handleSort('nazwa')} style={{ minWidth: '180px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Produkt <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="nazwa" />
              </th>
              <th onClick={() => handleSort('kod')} style={{ width: '100px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Kod/EAN <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="kod" />
              </th>
              <th onClick={() => handleSort('opakowanie')} style={{ width: '70px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Opak. <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="opakowanie" />
              </th>
              <th onClick={() => handleSort('cena_zakupu')} style={{ width: '75px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Zakup <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="cena_zakupu" />
              </th>
              <th onClick={() => handleSort('cena_sprzedazy')} style={{ width: '75px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Cena dom. <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="cena_sprzedazy" />
              </th>
              <th style={{ width: '75px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Cena spec.</th>
              <th onClick={() => handleSort('marza')} style={{ width: '50px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px', cursor: 'pointer', userSelect: 'none' }}>
                Marża <SortIcon sortColumn={sortColumn} sortDirection={sortDirection} column="marza" />
              </th>
              <th style={{ width: '50px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Status</th>
              <th style={{ width: '70px', padding: '0.3rem', fontWeight: '600', color: 'var(--text-secondary)', borderBottom: '2px solid var(--border)', fontSize: '10px' }}>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map((product, index) => {
              const lp = (currentPage - 1) * itemsPerPage + index + 1;
              const enrichedProduct = {
                ...product,
                simplifiedName: product.nazwa_uproszczona || simplifyProductName(product.nazwa, manufacturers),
                packageQuantity: extractPackageQuantity(product, product.nazwa, product.opis, measureUnits),
                weight: extractWeight(product.nazwa, product.opis),
                manufacturer: product.producent || extractManufacturer(product.nazwa, null, manufacturers)
              };

              return (
                <React.Fragment key={product.id}>
                  <tr style={{
                    backgroundColor: selectedProducts.has(product.id)
                      ? 'var(--info-light)'
                      : editingCenowkaProductId === product.id
                        ? 'var(--warning-light)'
                        : (index % 2 === 0 ? 'var(--bg)' : 'var(--card-bg)' ),
                    borderBottom: '1px solid var(--border)',
                    borderLeft: selectedProducts.has(product.id)
                      ? '3px solid var(--info)'
                      : editingCenowkaProductId === product.id
                        ? '3px solid var(--warning)'
                        : 'none',
                    lineHeight: '1.2'
                  }}>
                    <td style={{ padding: '0.25rem 0.2rem', textAlign: 'center', verticalAlign: 'middle' }}>
                      <input type="checkbox" className="form-check-input"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                        style={{ margin: 0, width: '14px', height: '14px' }} />
                    </td>

                    {/* LP */}
                    <td style={{ padding: '0.25rem 0.15rem', textAlign: 'center', fontWeight: '500', color: 'var(--text-muted)', fontSize: '9px', verticalAlign: 'middle' }}>
                      {lp}
                    </td>

                    {/* Producent */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      {editingProductManufacturer === product.id ? (
                        <select className="form-select form-select-sm"
                          value={product.producent_id || ''}
                          onChange={(e) => handleSingleManufacturerChange(product.id, e.target.value)}
                          style={{ fontSize: '10px', padding: '2px 4px', height: '22px' }}>
                          <option value="">Brak</option>
                          {manufacturers.map((manufacturer) => (
                            <option key={manufacturer.id} value={manufacturer.id}>{manufacturer.nazwa}</option>
                          ))}
                        </select>
                      ) : (
                        <div
                          style={{ fontSize: '10px', fontWeight: '500', color: enrichedProduct.manufacturer ? 'var(--info)' : 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90px' }}
                          onClick={() => setEditingProductManufacturer(product.id)}
                          title={enrichedProduct.manufacturer || manufacturers.find(m => m.id === product.producent_id)?.nazwa || 'Brak'}>
                          {enrichedProduct.manufacturer || manufacturers.find(m => m.id === product.producent_id)?.nazwa || '—'}
                        </div>
                      )}
                    </td>

                    {/* Produkt */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text)', lineHeight: '1.25', maxHeight: '2.5em', overflow: 'hidden' }} title={product.nazwa}>
                        {product.nazwa}
                      </div>
                      <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>VAT: {product.stawka_vat || 23}%</span>
                    </td>

                    {/* Kod/EAN */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--success)', fontWeight: '500' }}>{product.kod_produktu}</div>
                      {product.ean && <div style={{ fontSize: '9px', fontFamily: 'monospace', color: 'var(--info)' }}>{product.ean}</div>}
                    </td>

                    {/* Opakowanie */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle', fontSize: '10px', color: 'var(--text-secondary)' }}>
                      {enrichedProduct.packageQuantity || '—'}
                    </td>

                    {/* Cena zakupu */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      {product.purchase_price && parseFloat(product.purchase_price) > 0 ? (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--orange)' }}>
                            {(parseFloat(product.purchase_price) * (1 + (parseFloat(product.stawka_vat) || 23) / 100)).toFixed(2)} zł
                          </div>
                          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                            n: {parseFloat(product.purchase_price).toFixed(2)}
                          </div>
                        </div>
                      ) : <span style={{ fontSize: '10px', color: 'var(--border)' }}>{'—'}</span>}
                    </td>

                    {/* Cena domyślna */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text)' }}>
                        {parseFloat(product.cena_sprzedazy_brutto || 0).toFixed(2)} zł
                      </div>
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        n: {parseFloat(product.cena_sprzedazy_netto || 0).toFixed(2)}
                      </div>
                    </td>

                    {/* Cena specjalna — dwuklik = edycja inline (brutto) */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle', cursor: canEditPrices ? 'pointer' : 'default' }}
                      title={canEditPrices ? 'Kliknij dwukrotnie, aby edytować cenę specjalną (brutto)' : undefined}
                      onDoubleClick={() => startInlinePriceEdit(product)}>
                      {editingPriceProductId === product.id ? (
                        <input
                          type="number" step="0.01" min="0.01" autoFocus
                          disabled={savingPrice}
                          value={editingPriceValue}
                          onChange={(e) => setEditingPriceValue(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitInlinePriceEdit(product); }
                            if (e.key === 'Escape') { e.preventDefault(); setEditingPriceProductId(null); }
                          }}
                          onBlur={() => commitInlinePriceEdit(product)}
                          style={{
                            width: '72px', padding: '0.2rem 0.3rem', fontSize: '11px', fontWeight: '600',
                            border: '2px solid var(--warning)', borderRadius: '4px',
                            backgroundColor: 'var(--card-bg)', color: 'var(--text)'
                          }}
                        />
                      ) : product.hasSpecialPrice ? (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--warning)' }}>
                            {parseFloat(product.specialPriceBrutto || 0).toFixed(2)} zł
                          </div>
                          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                            n: {parseFloat(product.specialPriceNetto || 0).toFixed(2)}
                          </div>
                        </div>
                      ) : <span style={{ fontSize: '9px', color: 'var(--border)' }}>{'—'}</span>}
                    </td>

                    {/* Marża — jedno zrodlo: displayMarginPercent z usePricingData (to samo dla sortowania i filtra progu) */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      {product.displayMarginPercent !== null && product.displayMarginPercent !== undefined ? (
                        <span style={{ fontSize: '11px', fontWeight: '600', color: parseFloat(product.displayMarginPercent) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                          {parseFloat(product.displayMarginPercent).toFixed(1)}%
                        </span>
                      ) : (
                        <span style={{ fontSize: '9px', color: 'var(--border)' }}>{'—'}</span>
                      )}
                    </td>

                    {/* Status */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      {product.hasSpecialPrice ? (
                        <span style={{ padding: '2px 6px', fontSize: '9px', backgroundColor: 'var(--warning)', color: 'var(--text)', borderRadius: '3px', fontWeight: '600' }}>Spec.</span>
                      ) : (
                        <span style={{ padding: '2px 6px', fontSize: '9px', backgroundColor: 'var(--border)', color: 'var(--text-secondary)', borderRadius: '3px' }}>Dom.</span>
                      )}
                    </td>

                    {/* Akcje - rozwijane menu */}
                    <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'middle' }}>
                      <div style={{ position: 'relative' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenActionMenu(openActionMenu === product.id ? null : product.id);
                          }}
                          style={{
                            padding: '4px 10px', fontSize: '11px',
                            border: '1px solid var(--purple)',
                            color: 'var(--purple)',
                            fontWeight: openActionMenu === product.id ? '700' : '400',
                            display: 'flex', alignItems: 'center', gap: '3px'
                          }}
                        >
                          {'⚙️'} Akcje {'▾'}
                        </button>

                        {openActionMenu === product.id && (
                          <div style={{
                            position: 'absolute', top: '100%', right: 0, zIndex: 1000,
                            backgroundColor: 'var(--card-bg)', borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            border: '1px solid var(--border)', minWidth: '180px',
                            marginTop: '4px', overflow: 'hidden'
                          }}>
                            {canViewHistory && (
                              <button onClick={() => { handleShowHistory(product); setOpenActionMenu(null); }}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--card-bg)', textAlign: 'left', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--bg)' }}>
                                <FaHistory style={{ color: 'var(--info)' }} /> Historia cen
                              </button>
                            )}
                            {canEditPrices && (
                              <button onClick={() => { handleDirectPriceEdit(product); setOpenActionMenu(null); }}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--card-bg)', textAlign: 'left', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--bg)' }}>
                                <FaMoneyBill style={{ color: 'var(--success)' }} /> Wpisz cenę
                              </button>
                            )}
                            {canEditPrices && (
                              <button onClick={() => { handleEditCenowka(product); setOpenActionMenu(null); }}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--card-bg)', textAlign: 'left', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--bg)' }}>
                                <FaTag style={{ color: 'var(--info)' }} /> Edytuj cenówkę
                              </button>
                            )}
                            {canEditPrices && (
                              <button onClick={() => { setSelectedProducts(new Set([product.id])); handleBulkPriceChange(); setOpenActionMenu(null); }}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--card-bg)', textAlign: 'left', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--bg)' }}>
                                <FaEdit style={{ color: 'var(--text-muted)' }} /> Kalkulatory cen
                              </button>
                            )}
                            {canPrintLabels && (
                              <button onClick={() => { addToBuffer(product); setOpenActionMenu(null); }}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--card-bg)', textAlign: 'left', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: product.hasSpecialPrice ? '1px solid var(--bg)' : 'none' }}>
                                <FaTag style={{ color: 'var(--warning)' }} /> Do bufora cenówek
                              </button>
                            )}
                            {product.hasSpecialPrice && canDeleteSpecialPrice && (
                              <button onClick={() => { setSelectedProducts(new Set([product.id])); handleRemoveSpecialPrices(); setOpenActionMenu(null); }}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', background: 'var(--danger-light)', textAlign: 'left', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--danger)' }}>
                                <FaTrash style={{ color: 'var(--danger)' }} /> Usuń cenę specjalną
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Inline edycja cenówki */}
                  {editingCenowkaProductId === product.id && (
                    <tr>
                      <td colSpan="12" style={{ padding: 0 }}>
                        <CenowkaInlineEditor
                          product={product}
                          {...cenowkaEditorProps}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PricingTable;
